// sales-leads-v1
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, Columns3, Copy, Download, ExternalLink, Globe, List, Loader2, Plus, Search, Sparkles, Target, Trophy, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { BranchTag } from "@/components/BranchTag";
import { LeadDialog } from "@/components/sales/LeadDialog";
import { ALL_STAGES, CSV_TEMPLATE, STAGES, boardStage, budget, dmy, followup, isDueToday, isOverdue, leadsFromCsv, stageLabel } from "@/components/sales/leadUtils";

export const LEAD_PATH = "/erp/delivery/sales-flow/leads";
const selectCls = "h-10 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";
const FU_TONE = { overdue: "text-red-600", today: "text-amber-600", later: "text-muted-foreground" };

function Stat({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`lead-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-secondary ${tone || "text-[#910029]"}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function FollowChip({ iso }) {
  const f = followup(iso);
  if (!f) return null;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${FU_TONE[f.tone]}`}><CalendarClock className="h-3 w-3" />{f.text}</span>;
}

function LeadCard({ lead, onOpen, onDragStart }) {
  return (
    <div role="button" tabIndex={0} draggable onDragStart={(e) => onDragStart(e, lead)} onClick={() => onOpen(lead)} onKeyDown={(e) => e.key === "Enter" && onOpen(lead)}
      data-testid="lead-card" className={`cursor-pointer rounded-xl border bg-card p-3 text-left shadow-sm transition hover:shadow-md ${lead.priority === "HIGH" ? "border-l-4 border-l-[#910029]" : "border-border"}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-foreground">{lead.title}</span>
        {lead.priority === "HIGH" && <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">High</Badge>}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{[lead.project_type, lead.location].filter(Boolean).join(" · ") || "—"}</div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm font-semibold tabular-nums">{budget(lead.estimated_value)}</span>
        <FollowChip iso={lead.next_followup_at} />
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <span className="truncate">{lead.source || "—"}</span><span className="ml-2 truncate">{lead.owner_name || "Unassigned"}</span>
      </div>
    </div>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setParsed(null); }, [open]);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setParsed({ name: file.name, ...leadsFromCsv(await file.text()) });
  };
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/pipeline/leads/bulk", { leads: parsed.leads.slice(0, 500) }); toast.success(`${data.created} leads imported`); onOpenChange(false); onDone(); }
    catch (ex) { toast.error(getErrorMessage(ex), { duration: 8000 }); } finally { setBusy(false); }
  };
  const template = () => {
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv" })); a.download = "leads-template.csv"; a.click();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import leads</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Upload a CSV with a header row. Columns: Name, Phone, Email, Location, Project type, Budget, Source, Requirement. Imported leads start as New. <button type="button" onClick={template} className="font-medium text-[#910029] underline">Download a template</button></p>
        <Input type="file" accept=".csv,text/csv" onChange={pick} data-testid="import-file" />
        {parsed && <p className="rounded-md bg-secondary px-3 py-2 text-sm" data-testid="import-summary">{parsed.leads.length} lead{parsed.leads.length === 1 ? "" : "s"} ready to import{parsed.skipped ? `, ${parsed.skipped} row(s) skipped (no name)` : ""}.</p>}
        <DialogFooter><Button disabled={!parsed || !parsed.leads.length || busy} onClick={go} data-testid="import-go">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Import</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebFormDialog({ open, onOpenChange }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/pipeline/lead-form-link").then(({ data }) => setUrl(`${window.location.origin}/lead-form/${data.token}`)).catch((ex) => toast.error(getErrorMessage(ex)));
  }, [open]);
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Select the link and copy it"); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="webform-dialog">
        <DialogHeader><DialogTitle>Web form</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Share this link on your website, WhatsApp or social pages. Whatever people submit lands here as a New lead, and the admins are notified.</p>
        <div className="flex gap-2"><Input readOnly value={url} onFocus={(e) => e.target.select()} data-testid="webform-url" /><Button type="button" variant="outline" onClick={copy} disabled={!url}><Copy className="h-4 w-4" /> Copy</Button></div>
        <DialogFooter><Button variant="outline" asChild disabled={!url}><a href={url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Open form</a></Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The leads workspace of the Sales Flow: stats, filters, Board / List, add, import, export, web form. `closed` = won / lost leads only. */
export default function LeadsBoard({ closed = false, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [leads, setLeads] = useState([]);
  const [owners, setOwners] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("ALL");
  const [owner, setOwner] = useState("ALL");
  const [stage, setStage] = useState("ALL");
  const [view, setView] = useState(() => { try { return localStorage.getItem("leads-view") || "board"; } catch { return "board"; } });
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [webform, setWebform] = useState(false);
  const [over, setOver] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pipeline/leads"); setLeads(data);
    } catch (ex) { toast.error(getErrorMessage(ex)); } finally { setLoading(false); }
    onChanged && onChanged();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/pipeline/owners").then(({ data }) => setOwners(data)).catch(() => {});
    api.get("/revenue/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);
  const setViewSaved = (v) => { setView(v); try { localStorage.setItem("leads-view", v); } catch { /* ignore */ } };

  const open = (l) => navigate(`${LEAD_PATH}/${l.id}`);
  const stats = useMemo(() => {
    const live = leads.filter((l) => !["WON", "LOST"].includes(l.stage));
    const won = leads.filter((l) => l.stage === "WON"), lost = leads.filter((l) => l.stage === "LOST");
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    return {
      open: live.length, pipeline: live.reduce((s, l) => s + Number(l.estimated_value || 0), 0),
      today: live.filter((l) => isDueToday(l.next_followup_at)).length, overdue: live.filter((l) => isOverdue(l.next_followup_at)).length,
      month: leads.filter((l) => l.created_at && new Date(l.created_at) >= monthStart).length,
      rate: won.length + lost.length ? Math.round((won.length / (won.length + lost.length)) * 100) : null, wonValue: won.reduce((s, l) => s + Number(l.estimated_value || 0), 0),
    };
  }, [leads]);

  const sources = useMemo(() => [...new Set(leads.map((l) => l.source).filter(Boolean))].sort(), [leads]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return leads.filter((l) => (closed ? ["WON", "LOST"].includes(l.stage) : !["WON", "LOST"].includes(l.stage) || (view === "list" && stage !== "ALL"))
      && (source === "ALL" || l.source === source)
      && (owner === "ALL" || (owner === "ME" ? l.owner_id === user?.id : owner === "NONE" ? !l.owner_id : l.owner_id === owner))
      && (stage === "ALL" || l.stage === stage)
      && (!t || `${l.title} ${l.contact_phone || ""} ${l.contact_email || ""} ${l.location || ""} ${l.project_type || ""} ${l.code}`.toLowerCase().includes(t)));
  }, [leads, q, source, owner, stage, closed, view, user]);

  const move = async (lead, to) => {
    if (boardStage(lead.stage) === to) return;
    const before = leads;
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, stage: to } : l)));   // optimistic
    try { await api.patch(`/pipeline/leads/${lead.id}`, { stage: to }); toast.success(`${lead.title} moved to ${stageLabel(to)}`); load(); }
    catch (ex) { setLeads(before); toast.error(getErrorMessage(ex), { duration: 8000 }); }
  };
  const onDragStart = (e, lead) => { e.dataTransfer.setData("text/plain", lead.id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = (e, to) => {
    e.preventDefault(); setOver(null);
    const lead = leads.find((l) => l.id === e.dataTransfer.getData("text/plain"));
    if (lead) move(lead, to);
  };

  const exportCsv = () => {
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = ["Code", "Name", "Phone", "Email", "Location", "Project type", "Budget", "Stage", "Priority", "Source", "Owner", "Next follow-up", "Requirement"];
    const rows = shown.map((l) => [l.code, l.title, l.contact_phone, l.contact_email, l.location, l.project_type, l.estimated_value, stageLabel(l.stage), l.priority, l.source, l.owner_name, l.next_followup_at ? dmy(l.next_followup_at) : "", l.requirement]);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([[head, ...rows].map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" })); a.download = "leads.csv"; a.click();
  };

  const showView = closed ? "list" : view;
  return (
    <div className="space-y-4" data-testid="leads-board">
      {!closed && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat icon={Target} label="Open leads" value={stats.open} sub={`${budget(stats.pipeline)} pipeline`} />
          <Stat icon={CalendarClock} label="Due today" value={stats.today} tone="text-sky-600" />
          <Stat icon={AlertTriangle} label="Overdue" value={stats.overdue} tone="text-red-600" />
          <Stat icon={Sparkles} label="New this month" value={stats.month} tone="text-violet-600" />
          <Stat icon={Trophy} label="Win rate" value={stats.rate == null ? "—" : `${stats.rate}%`} sub={stats.wonValue ? `${budget(stats.wonValue)} won` : undefined} tone="text-emerald-600" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, place..." className="pl-9" data-testid="lead-search" />
        </div>
        <select className={selectCls} value={source} onChange={(e) => setSource(e.target.value)} data-testid="lead-source-filter" aria-label="Source">
          <option value="ALL">All sources</option>{sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={selectCls} value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="lead-owner-filter" aria-label="Owner">
          <option value="ALL">Everyone</option><option value="ME">My leads</option><option value="NONE">Unassigned</option>
          {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {showView === "list" && !closed && (
          <select className={selectCls} value={stage} onChange={(e) => setStage(e.target.value)} data-testid="lead-stage-filter" aria-label="Stage">
            <option value="ALL">Open stages</option>{ALL_STAGES.filter((s) => s.key !== "TENDER").map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {!closed && (
            <div className="flex overflow-hidden rounded-lg border border-border" role="group" aria-label="View">
              <button type="button" onClick={() => setViewSaved("board")} data-testid="lead-view-board" aria-pressed={view === "board"} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wide ${view === "board" ? "bg-[#910029] text-white" : "bg-background text-muted-foreground hover:bg-secondary"}`}><Columns3 className="h-3.5 w-3.5" /> Board</button>
              <button type="button" onClick={() => setViewSaved("list")} data-testid="lead-view-list" aria-pressed={view === "list"} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wide ${view === "list" ? "bg-[#910029] text-white" : "bg-background text-muted-foreground hover:bg-secondary"}`}><List className="h-3.5 w-3.5" /> List</button>
            </div>
          )}
          <Button variant="outline" onClick={() => setWebform(true)} data-testid="lead-webform"><Globe className="h-4 w-4" /> Web form</Button>
          <Button variant="outline" onClick={() => setImporting(true)} data-testid="lead-import"><Upload className="h-4 w-4" /> Import</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="lead-export"><Download className="h-4 w-4" /> Export</Button>
          <Button onClick={() => setAdding(true)} data-testid="lead-add"><Plus className="h-4 w-4" /> Add lead</Button>
        </div>
      </div>

      {loading ? <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p> : showView === "board" ? (
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="lead-board">
          {STAGES.map((s) => {
            const col = shown.filter((l) => boardStage(l.stage) === s.key);
            return (
              <div key={s.key} data-testid={`lead-col-${s.key}`} onDragOver={(e) => { e.preventDefault(); setOver(s.key); }} onDragLeave={() => setOver(null)} onDrop={(e) => onDrop(e, s.key)}
                className={`min-w-[230px] flex-1 basis-0 rounded-2xl border p-2.5 ${over === s.key ? "border-[#910029] bg-[#910029]/5" : "border-border bg-secondary/40"}`}>
                <div className="mb-2 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.dot }} />{s.label}<span className="text-muted-foreground">{col.length}</span>
                </div>
                <div className="space-y-2">
                  {col.length === 0 && <div className="rounded-xl border border-dashed border-border py-5 text-center text-xs text-muted-foreground">Drop here</div>}
                  {col.map((l) => <LeadCard key={l.id} lead={l} onOpen={open} onDragStart={onDragStart} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card">
          {shown.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground" data-testid="lead-empty">{closed ? "No won or lost leads yet." : "No leads match."}</p> : (
            <Table data-testid="lead-list">
              <TableHeader><TableRow className="bg-secondary/60">
                {["Code", "Name", "Contact", "Project", "Budget", "Stage", "Priority", "Owner", "Follow-up"].map((h) => <TableHead key={h}>{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {shown.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer" onClick={() => open(l)} data-testid="lead-row">
                    <TableCell className="font-mono text-xs">{l.code}<BranchTag row={l} /></TableCell>
                    <TableCell className="font-medium text-foreground">{l.title}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.contact_phone || l.contact_email || "—"}</TableCell>
                    <TableCell className="text-xs">{[l.project_type, l.location].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="tabular-nums">{budget(l.estimated_value)}</TableCell>
                    <TableCell><Badge variant={l.stage === "WON" ? "default" : l.stage === "LOST" ? "destructive" : "secondary"}>{stageLabel(l.stage)}</Badge></TableCell>
                    <TableCell className="text-xs">{l.priority ? l.priority[0] + l.priority.slice(1).toLowerCase() : "—"}</TableCell>
                    <TableCell className="text-xs">{l.owner_name || "—"}</TableCell>
                    <TableCell><FollowChip iso={l.next_followup_at} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <LeadDialog open={adding} onOpenChange={setAdding} lead={null} owners={owners} clients={clients} onSaved={load} />
      <ImportDialog open={importing} onOpenChange={setImporting} onDone={load} />
      <WebFormDialog open={webform} onOpenChange={setWebform} />
    </div>
  );
}
